import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Upload } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import ConfirmationModal from "../components/common/ConfirmationModal";
import BulkDeletionJobsPanel from "../components/Jobs/BulkDeletionJobsPanel";
import JobsRepository from "../repositories/JobsRepository";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { UserRole } from "../models/User";
import {
  BULK_DELETION_FAMILIES,
  BULK_DELETION_MAX_CSV_BYTES,
  BulkDeletionFamilyKey,
  BulkDeletionMode,
  bulkDeletionModeLabel,
  formatBytesAsMb,
} from "../models/Jobs/BulkDeletion";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

interface BulkDeletePageProps {
  userRole: UserRole;
  organizationName?: string;
  organizationId?: string;
}

const FAMILY_KEYS = Object.keys(BULK_DELETION_FAMILIES) as BulkDeletionFamilyKey[];

const FAMILY_LABELS: Record<BulkDeletionFamilyKey, string> = {
  cases: "Cases",
  intakes: "Intakes",
  providers: "Providers",
  templates: "Templates",
  firmDocuments: "Firm documents",
};

/**
 * Deletes whatever an uploaded list of database ids names, for one organization.
 */
/*
 * A management tool rather than part of a family's own deletion view: a list of file ids spans however many
 * entities the operator put in it, so there is no single case or provider it belongs under.
 */
const BulkDeletePage: React.FC<BulkDeletePageProps> = ({
  userRole,
  organizationName,
  organizationId,
}) => {
  const [family, setFamily] = useState<BulkDeletionFamilyKey>("cases");
  const [mode, setMode] = useState<BulkDeletionMode>("entities");
  const [recordsOnly, setRecordsOnly] = useState(false);
  const [reason, setReason] = useState("");
  const [csv, setCsv] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploaded, setUploaded] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  const canRun = userRole === UserRole.Admin || userRole === UserRole.Editor;
  const target = bulkDeletionModeLabel(family, mode);
  const canSubmit = !!organizationId && csv !== null && reason.trim().length > 0 && !submitting && canRun;

  const submit = async () => {
    if (!organizationId || !csv) return;
    if (csv.size === 0) {
      toast.error("That file is empty.");
      return;
    }
    if (csv.size > BULK_DELETION_MAX_CSV_BYTES) {
      toast.error(
        `That list is ${formatBytesAsMb(csv.size)}, over the ${formatBytesAsMb(BULK_DELETION_MAX_CSV_BYTES)} `
          + "one upload can carry. Split it and run the parts one after another."
      );
      return;
    }
    setSubmitting(true);
    setUploaded(0);
    try {
      await jobsRepository.startBulkDeletion(organizationId, family, mode, csv, reason.trim(), recordsOnly, setUploaded);
      toast.success(
        `Deletion of the uploaded ${target.toLowerCase()} started${recordsOnly ? ", records only" : ""}.`
      );
      setCsv(null);
      setReason("");
      // Back to the default, so the next list does not leave SharePoint copies behind by accident.
      setRecordsOnly(false);
      if (fileInput.current) fileInput.current.value = "";
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BULK_DELETION_JOBS, organizationId] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to start the deletion.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // The page scrolls rather than squeezing the runs list: the form above it keeps its height, so on a short window
    // the list would otherwise shrink until its rows, and the buttons on them, are clipped away.
    <div className="flex flex-1 flex-col gap-4 p-4 min-h-0 overflow-y-auto">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold flex items-center gap-2">Bulk delete from a list of ids<DocsHelpLink to="how-to/bulk-delete" /></h2>
        <p className="text-sm text-muted-foreground">
          Uploads a CSV of database ids and deletes what they name from{" "}
          <span className="font-medium">{organizationName || "the selected organization"}</span>, along with
          the copies in SharePoint unless you choose to delete records only.
        </p>
      </div>

      <Alert>
        <AlertTriangle className="size-4" />
        <AlertDescription>
          This cannot be undone. Ids that do not resolve are reported rather than stopping the run, so check
          the run's report afterwards to see what was skipped.
        </AlertDescription>
      </Alert>

      <Card className="flex flex-col gap-4 p-4">
        <div className="flex flex-col gap-2">
          <Label className="text-sm">Entity type</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={family}
            onValueChange={(next) => {
              if (next) setFamily(next as BulkDeletionFamilyKey);
            }}
            disabled={submitting}
            className="flex-wrap justify-start"
          >
            {FAMILY_KEYS.map((key) => (
              <ToggleGroupItem key={key} value={key}>
                {FAMILY_LABELS[key]}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">The uploaded ids name</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={mode}
            onValueChange={(next) => {
              if (next) setMode(next as BulkDeletionMode);
            }}
            disabled={submitting}
            className="flex-wrap justify-start"
          >
            <ToggleGroupItem value="entities">{bulkDeletionModeLabel(family, "entities")}</ToggleGroupItem>
            <ToggleGroupItem value="files">{bulkDeletionModeLabel(family, "files")}</ToggleGroupItem>
          </ToggleGroup>
          <span className="text-xs text-muted-foreground">
            {mode === "entities"
              ? `Each id is a ${FAMILY_LABELS[family].toLowerCase().replace(/s$/, "")} to delete, along with all of its files.`
              : "Each id is a single file to delete. Its parent is left alone."}
          </span>
        </div>

        <div className="flex flex-col gap-2">
          <Label className="text-sm">SharePoint copies</Label>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={recordsOnly ? "keep" : "delete"}
            onValueChange={(next) => {
              if (next) setRecordsOnly(next === "keep");
            }}
            disabled={submitting}
            className="flex-wrap justify-start"
          >
            <ToggleGroupItem value="delete">Delete them</ToggleGroupItem>
            <ToggleGroupItem value="keep">Leave them, delete records only</ToggleGroupItem>
          </ToggleGroup>
          <span className={`text-xs ${recordsOnly ? "text-destructive" : "text-muted-foreground"}`}>
            {recordsOnly
              ? "Only NFS's records are deleted, and SharePoint is not contacted. Use this when the copies are "
                + "already gone from SharePoint: any that still exist stay there, and NFS can no longer find them."
              : "Each item's copy in SharePoint is deleted first, then NFS's records."}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-sm" htmlFor="bulk-deletion-csv">
            List of ids
          </Label>
          <Input
            id="bulk-deletion-csv"
            ref={fileInput}
            type="file"
            accept=".csv,text/csv,text/plain"
            disabled={submitting}
            onChange={(event) => setCsv(event.target.files?.[0] ?? null)}
          />
          <span className="text-xs text-muted-foreground">
            One id per line. An <code>id</code> header, blank lines, and extra columns are ignored. Up to{" "}
            {formatBytesAsMb(BULK_DELETION_MAX_CSV_BYTES)} per upload.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <Label className="text-sm" htmlFor="bulk-deletion-reason">
            Reason
          </Label>
          <Input
            id="bulk-deletion-reason"
            value={reason}
            placeholder="Why these are being deleted"
            disabled={submitting}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <Button disabled={!canSubmit} onClick={() => setConfirmOpen(true)}>
          <Upload className="size-4" />
          {submitting ? "Starting…" : `Delete uploaded ${target.toLowerCase()}`}
        </Button>
        {submitting && (
          <div className="flex flex-col gap-1">
            <Progress value={uploaded} />
            <span className="text-xs text-muted-foreground">
              {uploaded < 100
                ? `Uploading the list — ${uploaded}%`
                : "List uploaded. Preparing the run, which takes a while for a long list."}
            </span>
          </div>
        )}
        {!canRun && (
          <span className="text-xs text-destructive">
            Deleting from a list requires an editor or admin role.
          </span>
        )}
      </Card>

      <BulkDeletionJobsPanel organizationId={organizationId} />

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          await submit();
        }}
        message={
          recordsOnly
            ? `Delete NFS's records of every ${target.toLowerCase().replace(/s$/, "")} in this list, leaving SharePoint untouched? This cannot be undone.`
            : `Delete every ${target.toLowerCase().replace(/s$/, "")} in this list? This cannot be undone.`
        }
        statusColor="danger"
        buttonText="Delete"
      >
        <div className="mt-4 flex flex-col gap-3">
          <div>
            <p className="text-sm font-semibold">Organization</p>
            <p className="text-sm text-muted-foreground">{organizationName || "Unknown"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Deleting</p>
            <p className="text-sm text-muted-foreground">{target}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">SharePoint copies</p>
            {recordsOnly ? (
              <p className="text-sm text-destructive">
                Left in place. Any that still exist stay in SharePoint, and NFS can no longer find them.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">Deleted</p>
            )}
          </div>
          <div>
            <p className="text-sm font-semibold">List</p>
            <p className="text-sm text-muted-foreground">{csv?.name ?? "None selected"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Reason</p>
            <p className="text-sm text-muted-foreground">{reason}</p>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default BulkDeletePage;
